import styled from "@emotion/styled";
import { useQuery } from "@tanstack/react-query";
import { useState, type ReactNode } from "react";

import type * as auth from "@skilitics/auth-client";
import getAuthClient from "@skilitics/designer-auth";
import { useCognitoUsername } from "@skilitics/designer-auth/state";
import {
  Button,
  IconButton,
  PageSection,
  PrefixChip,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  toast,
  Tooltip,
} from "@skilitics/designer-ui-components";
import { useFormatMessage } from "@skilitics/localization";

import { useWorkspaceInfo } from "../../../../auth/workspaces.ts";
import { ExpectedError, membersQueryOptions } from "../queries.ts";
import { RouteErrorView } from "../RouteErrorView.tsx";
import { ConfirmCancelInviteDialog } from "./dialogs/ConfirmCancelInviteDialog.tsx";
import { ConfirmRemoveMemberDialog } from "./dialogs/ConfirmRemoveMemberDialog.tsx";
import { ConfirmResendInviteDialog } from "./dialogs/ConfirmResendInviteDialog.tsx";
import { InviteMembersDialog } from "./dialogs/InviteMembersDialog.tsx";

// slightly paranoid, verify value is valid for WorkspaceRole
interface RoleOption {
  readonly value: "admin" | "edit";
  readonly label: string;
}

const roleOptions: readonly RoleOption[] = [
  { value: "admin", label: "Admin" },
  { value: "edit", label: "Editor" },
];

export const WorkspaceMembersPage = () => {
  const formatMessage = useFormatMessage(formatStrings);

  const cognitoUsername = useCognitoUsername();
  const workspace = useWorkspaceInfo();

  // useModalDialog adds an extra <ModalDialog /> which makes it a pain to
  // factor out the whole dialog so you get HMR etc.
  const [dialog, setDialog] = useState<ReactNode>(null);
  const closeDialog = () => setDialog(null);

  // We currently just refetch when we update anything, could be optimized.
  const { data, error, isLoading, refetch } = useQuery(membersQueryOptions);

  // Other errors will get a sentry error dialog by default
  if (error instanceof ExpectedError) {
    return <RouteErrorView code={error.code} />;
  }

  return (
    <PageSection
      header={
        <Header>
          <div>
            <h3>{formatMessage("header")}</h3>
            <p>{formatMessage("description")}</p>
          </div>
          <Button
            variant="contained"
            disabled={!data}
            onClick={() => {
              setDialog(
                <InviteMembersDialog
                  orgName={workspace?.name ?? ""}
                  existing={data!}
                  commit={async (recipients) => {
                    await getAuthClient().inviteMembers(recipients);
                    await refetch();
                  }}
                  close={closeDialog}
                />,
              );
            }}
          >
            {formatMessage("inviteLabel")}
          </Button>
        </Header>
      }
    >
      <Table stickyHeader data-test="members-table">
        <StyledTableHead>
          <TableRow>
            <StyledTableCell>{formatMessage("statusLabel")}</StyledTableCell>
            <StyledTableCell>{formatMessage("memberLabel")}</StyledTableCell>
            <StyledTableCell>{formatMessage("roleLabel")}</StyledTableCell>
            <StyledTableCell width={100}>{formatMessage("actionsLabel")}</StyledTableCell>
          </TableRow>
        </StyledTableHead>
        <TableBody>
          {isLoading && (
            <TableRow>
              <StyledTableCell colSpan={4} align="center">
                Loading...
              </StyledTableCell>
            </TableRow>
          )}
          {data?.invites.map((invite) => {
            const resendInvite = () => {
              setDialog(
                <ConfirmResendInviteDialog
                  invite={invite}
                  confirm={async () => {
                    await getAuthClient().resendInvite(invite.email);
                    toast.success(`Resent invite to ${invite.email}`);
                    await refetch();
                  }}
                  close={closeDialog}
                />,
              );
            };

            const cancelInvite = () => {
              setDialog(
                <ConfirmCancelInviteDialog
                  invite={invite}
                  confirm={async () => {
                    await getAuthClient().cancelInvite(invite.email);
                    await refetch();
                  }}
                  close={closeDialog}
                />,
              );
            };

            return (
              <TableRow key={invite.email}>
                <StyledTableCell>
                  <PrefixChip type="warning" label={formatMessage("statusPending")} />
                </StyledTableCell>
                <StyledTableCell align="left">
                  <MemberEmail>{invite.email}</MemberEmail>
                </StyledTableCell>
                <StyledTableCell>
                  <Select
                    value={invite.role}
                    options={roleOptions}
                    onChange={async (event) => {
                      const value = event.target.value as auth.Role;
                      await getAuthClient().updateInviteRole(invite.email, value);
                      await refetch();
                    }}
                  />
                </StyledTableCell>
                <StyledTableCell>
                  <Tooltip title={formatMessage("resendInviteTooltip")}>
                    <IconButton icon="envelope" data-test="resend-invite-button" onClick={resendInvite} />
                  </Tooltip>
                  <Tooltip title={formatMessage("cancelInviteTooltip")}>
                    <IconButton icon="trash-alt" data-test="cancel-invite-button" onClick={cancelInvite}></IconButton>
                  </Tooltip>
                </StyledTableCell>
              </TableRow>
            );
          })}
          {data?.members.map((member) => {
            const isSelf = cognitoUsername === member.username;

            const removeMember = () => {
              setDialog(
                <ConfirmRemoveMemberDialog
                  member={member}
                  confirm={async () => {
                    await getAuthClient().removeMember(member.username);
                    await refetch();
                  }}
                  close={closeDialog}
                />,
              );
            };

            return (
              <TableRow key={member.username}>
                <StyledTableCell>
                  <PrefixChip type="primary" label={formatMessage("statusActive")} />
                </StyledTableCell>
                <StyledTableCell>
                  <MemberDetails>
                    <MemberName>
                      {member.given_name} {member.family_name}
                    </MemberName>
                    <MemberEmail>{member.email}</MemberEmail>
                  </MemberDetails>
                </StyledTableCell>
                <StyledTableCell>
                  <Tooltip title={isSelf ? formatMessage("updateRoleSelfTooltip") : ""}>
                    <Select
                      disabled={isSelf}
                      value={member.role}
                      options={roleOptions}
                      onChange={async (event) => {
                        const value = event.target.value as auth.Role;
                        await getAuthClient().updateMemberRole(member.username, value);
                        await refetch();
                      }}
                    />
                  </Tooltip>
                </StyledTableCell>
                <StyledTableCell>
                  <Tooltip title={formatMessage(isSelf ? "removeMemberSelfTooltip" : "removeMemberTooltip")}>
                    <span>
                      <IconButton
                        disabled={isSelf}
                        icon="trash-alt"
                        data-test="remove-member-button"
                        onClick={removeMember}
                      />
                    </span>
                  </Tooltip>
                </StyledTableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      {dialog}
    </PageSection>
  );
};

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const StyledTableHead = styled(TableHead)`
  th {
    font-size: 14px;
    font-weight: 500;
    background: ${({ theme }) => theme.surfaceSoft};
    color: ${({ theme }) => theme.generalTextIcon};
  }
`;

const StyledTableCell = styled(TableCell)`
  :first-of-type {
    padding-inline-start: 25px;
  }
  :last-of-type {
    padding-inline-end: 25px;
  }
`;

const MemberDetails = styled.div`
  display: flex;
  flex-direction: column;
`;

const MemberName = styled.div`
  font-weight: 500;
`;

const MemberEmail = styled.div``;

const formatStrings = {
  header: {
    en: "Workspace Members",
  },
  description: {
    en: "Manage the members within this workspace.",
  },

  inviteLabel: { en: "Invite New Members" },

  statusLabel: { en: "Status" },
  memberLabel: { en: "Member" },
  roleLabel: { en: "Role" },
  actionsLabel: { en: "Actions" },

  statusActive: { en: "Active" },
  statusPending: { en: "Pending" },

  updateRoleSelfTooltip: { en: "As an admin, you can't modify your own role" },
  removeMemberSelfTooltip: { en: "As an admin, you can't remove your own membership" },

  resendInviteTooltip: { en: "Resend Invite" },
  cancelInviteTooltip: { en: "Cancel Invite" },
  removeMemberTooltip: { en: "Remove Member" },
};
